import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

const unifiedButtonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[1.3rem] font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brite-terra/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-brite-card border border-brite-line text-foreground ' +
          'hover:text-brite-terra hover:border-brite-terra ' +
          'active:scale-[0.98]',
        primary:
          'bg-brite-ink text-brite-paper font-semibold ' +
          'hover:bg-brite-terra ' +
          'active:scale-[0.98]',
        secondary:
          'bg-brite-sand text-brite-ink font-semibold border border-brite-line ' +
          'hover:border-brite-terra hover:text-brite-terra ' +
          'active:scale-[0.98]',
        outline:
          'bg-transparent border border-brite-line text-foreground backdrop-blur-sm ' +
          'hover:bg-glass-light hover:border-brite-terra/50 hover:text-brite-terra ' +
          'active:scale-[0.98]',
        ghost:
          'text-foreground/90 ' +
          'hover:bg-glass-light hover:text-brite-terra hover:backdrop-blur-sm ' +
          'active:scale-[0.98]',
        danger:
          'bg-destructive/10 text-destructive border border-destructive/20 ' +
          'hover:bg-destructive hover:text-destructive-foreground hover:border-destructive ' +
          'hover:shadow-[0_0_20px_rgba(220,38,38,0.4)] ' +
          'active:scale-[0.98]',
        destructive:
          'bg-destructive/10 text-destructive border border-destructive/20 ' +
          'hover:bg-destructive hover:text-destructive-foreground hover:border-destructive ' +
          'hover:shadow-[0_0_20px_rgba(220,38,38,0.4)] ' +
          'active:scale-[0.98]',
        link: 'text-brite-terra underline-offset-4 hover:underline hover:text-brite-forest',
      },
      size: {
        sm: 'min-h-[44px] px-3 py-2 text-sm',
        md: 'min-h-[44px] px-4 py-2.5 text-base',
        lg: 'min-h-[52px] px-6 py-3 text-lg',
        icon: 'h-11 w-11 p-0',
        'icon-sm': 'h-6 w-6 min-h-0 p-0', // Small inline icon button
        inline: 'h-auto min-h-0 p-0', // Inline text-style button (no padding/height constraints)
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      fullWidth: false,
    },
  },
);

export interface UnifiedButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof unifiedButtonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  'data-testid'?: string;
}

const UnifiedButton = React.forwardRef<HTMLButtonElement, UnifiedButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      children,
      type = 'button',
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Determine if button should be disabled
    const isDisabled = disabled || loading;

    // Build the icon elements
    const leftIconElement = loading ? <Loader2 className="animate-spin" /> : leftIcon;

    // When using asChild with Slot, we need to ensure single child
    const content = asChild ? (
      children
    ) : (
      <>
        {leftIconElement}
        {children}
        {!loading && rightIcon}
      </>
    );

    return (
      <Comp
        type={type}
        className={cn(
          unifiedButtonVariants({ variant, size, fullWidth, className }),
          loading && 'cursor-wait',
        )}
        ref={ref}
        disabled={isDisabled}
        data-testid={dataTestId}
        aria-busy={loading}
        {...props}
      >
        {content}
      </Comp>
    );
  },
);
UnifiedButton.displayName = 'UnifiedButton';

// Export additional utilities for external use
export { UnifiedButton, unifiedButtonVariants };
